import * as y from "yup";

const mediaSchema = y.object({
  portrait: y.array().of(
    y.object({
      hash: y.string().nullable(),
      picture_url: y.string().nullable(),
      fileKey: y.string().nullable(),
      name: y.string().nullable(),
      description: y.string().nullable(),
      type: y.string().nullable()
    })
  ).default([]),
  square: y.array().of(
    y.object({
      hash: y.string().nullable(),
      picture_url: y.string().nullable(),
      fileKey: y.string().nullable(),
      name: y.string().nullable(),
      description: y.string().nullable(),
      type: y.string().nullable()
    })
  ).default([]),
  // rectangle: y.array().of(
  //   y.object({
  //     hash: y.string().nullable(),
  //     picture_url: y.string().nullable(),
  //     fileKey: y.string().nullable(),
  //     name: y.string().nullable(),
  //     description: y.string().nullable(),
  //     type: y.string().nullable()
  //   })
  // ).default([])
});

const videoSchema = y.object({
  portrait: y.array().of(
    y.object({
      id: y.string().nullable(),
      external_id: y.string().nullable(),
      thumbnail_url: y.string().nullable(),
      source: y.string().nullable(),
      fileKey: y.string().nullable(),
      type: y.string().nullable()
    })
  ).default([]),
  square: y.array().of(
    y.object({
      id: y.string().nullable(),
      external_id: y.string().nullable(),
      thumbnail_url: y.string().nullable(),
      source: y.string().nullable(),
      fileKey: y.string().nullable(),
      type: y.string().nullable()
    })
  ).default([]),
  // rectangle: y.array().of(
  //   y.object({
  //     id: y.string().nullable(),
  //     external_id: y.string().nullable(),
  //     thumbnail_url: y.string().nullable(),
  //     source: y.string().nullable(),
  //     fileKey: y.string().nullable(),
  //     type: y.string().nullable()
  //   })
  // ).default([])
});

export const AdCreativeFormSchema = y.object({
  id: y.string().nullable(),
  name: y.string().required("Please provide a name for the creative"),
  external_id: y.string().nullable(),
  adset_id: y.string().nullable(),
  event_id: y.string().required("Event ID is required"),
  media_type: y.string()
    .required("Please select a media type")
    .oneOf(['single', 'carousel'], "Media type must be either single or carousel"),
  message: y.string().required("Please provide a message for the creative").min(1, "Message cannot be empty"),
  status: y.string().nullable(),
  campaign_id: y.string().required("Campaign ID is required"),
  page_id: y.object({
    value: y.string().required("Please select a Facebook page"),
    label: y.string().required("Page label is required")
  }).required("Please select a Facebook page").nullable(),
  instagram_actor_id: y.object({
    value: y.string().required("Please select an Instagram account"),
    label: y.string().required("Instagram account label is required")
  }).required("Please select an Instagram account").nullable(),
  images: mediaSchema.test({
    name: 'images-validation',
    message: 'Invalid images configuration',
    test: function(images) {
      const { parent } = this;
      const hasImages = Object.values(images || {}).some(arr => arr.length > 0);
      //@ts-ignore
      const hasVideos = Object.values(parent.videos || {}).some(arr => arr.length > 0);
      
      // Don't validate if using videos
      if (hasVideos) return true;
      
      // Check if we need media
      if (!hasImages && !hasVideos) {
        return this.createError({
          path: 'images',
          message: parent.media_type === 'single' 
            ? 'Please upload either an image or video for your ad'
            : 'Please upload at least 2 media items for your carousel'
        });
      }

      // For single media type, check if all ratios are present
      if (parent.media_type === 'single' && hasImages) {
        const missingRatios = ['square', 'portrait'].filter(
          ratio => !images[ratio]?.length
        );
        
        if (missingRatios.length > 0) {
          return this.createError({
            path: 'images',
            message: `Please upload images for all required ratios. Missing: ${missingRatios.join(', ')}`
          });
        }
      }

      // Check carousel requirements
      if (parent.media_type === 'carousel') {
        const total = Object.values(images || {}).reduce((acc, arr) => acc + arr.length, 0);
        if (total < 2 || total > 10) {
          return this.createError({
            path: 'images',
            message: `Carousel must have between 2 and 10 items (currently has ${total})`
          });
        }
      }

      return true;
    }
  }),
  videos: videoSchema.test({
    name: 'videos-validation',
    message: 'Invalid videos configuration',
    test: function(videos) {
      const { parent } = this;
      //@ts-ignore
      const hasImages = Object.values(parent.images || {}).some(arr => arr.length > 0);
      //@ts-ignore
      const hasVideos = Object.values(videos || {}).some(arr => arr.length > 0);
      
      // Don't validate if using images
      if (hasImages) return true;
      
      // Check if we need media
      if (!hasImages && !hasVideos) {
        return this.createError({
          path: 'videos',
          message: parent.media_type === 'single' 
            ? 'Please upload either an image or video for your ad'
            : 'Please upload at least 2 media items for your carousel'
        });
      }

      // For single media type, check if all ratios are present
      if (parent.media_type === 'single' && hasVideos && !parent.id) {
        const missingRatios = ['square', 'portrait'].filter(
          ratio => !videos[ratio]?.length
        );
        
        if (missingRatios.length > 0) {
          return this.createError({
            path: 'videos',
            message: `Please upload videos for all required ratios. Missing: ${missingRatios.join(', ')}`
          });
        }
      }

      // Check carousel requirements
      if (parent.media_type === 'carousel') {
        const total = Object.values(videos || {}).reduce((acc, arr) => acc + arr.length, 0);
        if (total < 2 || total > 10) {
          return this.createError({
            path: 'videos',
            message: `Carousel must have between 2 and 10 items (currently has ${total})`
          });
        }
      }

      return true;
    }
  })
});

// Type for the form data that matches the schema
export type AdCreativeFormData = y.InferType<typeof AdCreativeFormSchema>; 